import { Component, createMemo, createSignal } from "solid-js";
import { GAME_ROWS, NUM_GAMES } from "./constants";
import { useGamesDataContext } from "./GameDataProvider";
import { GameMode } from "./types";

const winHistoryIndices = [...Array(GAME_ROWS - (NUM_GAMES - 1)).keys()].map(
  (x) => x + (NUM_GAMES - 1)
);
const lossHistoryIndices = [...Array(NUM_GAMES).keys()]
  .map((x) => x + GAME_ROWS)
  .reverse();

type StatisticsProps = {
  mode: GameMode;
  onCloseStatistics: () => void;
};
const Statistics: Component<StatisticsProps> = (props) => {
  const [gamesData, gamesDataFuncs] = useGamesDataContext();

  const [lossDistributionOpen, setLossDistributionOpen] = createSignal(false);

  const mode = props.mode === "free" ? props.mode : "daily";

  const winMaxValue = createMemo(() =>
    Math.max(
      ...gamesData[mode].history.slice(NUM_GAMES - 1, GAME_ROWS),
      1
    )
  );
  const lossMaxValue = createMemo(() =>
    Math.max(...gamesData[mode].history.slice(GAME_ROWS), 1)
  );

  const numWins = createMemo(() =>
    gamesData[mode].history
      .slice(NUM_GAMES - 1, GAME_ROWS)
      .reduce((prev, num) => prev + num, 0)
  );

  const numLosses = createMemo(() =>
    gamesData[mode].history
      .slice(GAME_ROWS)
      .reduce((prev, num) => prev + num, 0)
  );

  const totalGames = createMemo(() => numWins() + numLosses());

  const currentGameNumCorrect = createMemo(() =>
    gamesData[mode].answersCorrect.reduce(
      (prev, correct) => (prev += correct >= 0 ? 1 : 0),
      0
    )
  );
  const currentGameMaxCorrect = createMemo(() =>
    Math.max(...gamesData[mode].answersCorrect)
  );
  const currentGameWin = createMemo(
    () =>
      gamesDataFuncs.isGameComplete(mode) &&
      currentGameNumCorrect() === NUM_GAMES
  );
  const currentGameLoss = createMemo(
    () =>
      gamesDataFuncs.isGameComplete(mode) &&
      currentGameNumCorrect() < NUM_GAMES
  );

  return (
    <div class="w-full h-full overflow-auto">
      <div class="max-w-[550px] w-full m-auto flex flex-row-reverse pr-4 pt-2">
        <button
          type="button"
          class="bg-white dark:bg-gray-800 p-1 rounded-full text-gray-900 hover:text-black dark:text-gray-400 dark:hover:text-white"
          onClick={props.onCloseStatistics}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div class="max-w-[550px] m-auto w-full px-6 mb-8">
        <div class="text-4xl mt-2 mb-4 text-center">
          Statistiques {mode === "daily" ? "quotidiennes" : "d'entrainement"}
        </div>
        <div class="flex flex-row">
          <div class="flex flex-col text-center flex-1">
            <span class="text-xl">{numWins() + numLosses()}</span>
            <span class="text-base text-gray-600 dark:text-gray-300">
              Joués
            </span>
          </div>
          <div class="flex flex-col text-center flex-1">
            <span class="text-xl">
              {Math.round(
                (totalGames() > 0 ? numWins() / totalGames() : 0) * 100
              )}
            </span>
            <span class="text-base text-gray-600 dark:text-gray-300">
              % Gagnés
            </span>
          </div>
          <div class="flex flex-col text-center flex-1">
            <span class="text-xl">{gamesData[mode].currentStreak}</span>
            <span class="text-base text-gray-600 dark:text-gray-300">
              Série
              <br />
              actuelle
            </span>
          </div>
          <div class="flex flex-col text-center flex-1">
            <span class="text-xl">{gamesData[mode].maxStreak}</span>
            <span class="text-base text-gray-600 dark:text-gray-300">
              Meilleure
              <br />
              Série
            </span>
          </div>
        </div>
        <div class="text-4xl mt-8 text-center">Distribution des victoires</div>
        <div class="text-lg mb-4 text-center">
          (# essais pour trouver les 4 mots)
        </div>
        <div class="text-lg">
          {winHistoryIndices.map((i) => (
            <div class="flex flex-row mb-1">
              <div class="mr-2">{i + 1}</div>
              <div
                class="min-w-min text-right px-2"
                classList={{
                  " text-black bg-box-correct":
                    currentGameWin() && currentGameMaxCorrect() === i,
                  "text-black bg-gray-300 dark:text-white dark:bg-gray-700": !(
                    currentGameWin() && currentGameMaxCorrect() === i
                  ),
                }}
                style={{
                  width:
                    (gamesData[mode].history[i] / winMaxValue()) * 100 +
                    "%",
                }}
              >
                {gamesData[mode].history[i]}
              </div>
            </div>
          ))}
        </div>
        {numLosses() > 0 && (
          <>
            <div class="flex flex-row text-base mt-6 mb-1 px-2">
              <div class="flex-1">Gagnés - {numWins()}</div>
              <div class="flex-1 text-right">{numLosses()} - Perdus</div>
            </div>
            <div
              class="text-base font-bold flex flex-row items-center cursor-pointer rounded-l-xl rounded-r-xl overflow-hidden"
              onClick={() => setLossDistributionOpen(!lossDistributionOpen())}
            >
              <div
                class="bg-box-correct h-6"
                style={{ width: (numWins() / totalGames()) * 100 + "%" }}
              />
              <div
                class="bg-rose-600 dark:bg-rose-800 text-right h-6"
                style={{ width: (numLosses() / totalGames()) * 100 + "%" }}
              />
            </div>
          </>
        )}
        {lossDistributionOpen() && (
          <>
            <div class="text-4xl mt-8 text-center">Distribution des défaites</div>
            <div class="text-lg mb-4 text-center">(# mots ratés)</div>
            <div class="text-lg">
              {lossHistoryIndices.map((i) => (
                <div class="flex flex-row mb-1">
                  <div class="mr-2">{NUM_GAMES - (i - GAME_ROWS)}</div>
                  <div
                    class="min-w-min text-right px-2"
                    classList={{
                      "text-white bg-rose-600 dark:bg-rose-800":
                        currentGameLoss() &&
                        currentGameNumCorrect() === i - GAME_ROWS,
                      "text-black bg-gray-300 dark:bg-gray-700 dark:text-white":
                        !(
                          currentGameLoss() &&
                          currentGameNumCorrect() === i - GAME_ROWS
                        ),
                    }}
                    style={{
                      width:
                        (gamesData[mode].history[i] / lossMaxValue()) *
                          100 +
                        "%",
                    }}
                  >
                    {gamesData[mode].history[i]}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Statistics;
